//@flow
import type { ThunkAction } from '../../core/types'
import type { Bid } from './types'
import { normalize } from 'normalizr'
import history from '../../core/history'
import { services } from '../../core/feathers'
import { sadd, srem, sdel } from '../../modules/set'
import { batch } from '../../modules/lib/batchReducer'
import { mergeEntities, getDomain, removeAll } from '../../modules/entities'
import {
  startCommunication,
  endCommunication,
  isCommInProgress
} from '../../modules/communication'
import { schemas, domains } from '../constants'
import {
  BIDDING_SET,
  CREATE_BID_REQUEST,
  CREATE_BID_SUCCESS,
  CREATE_BID_FAILED,
  CLEAR_BIDS,
  CLEAR_BIDS_FOR_AUCTION
} from './constants'
import { getWinningItemIds, getOutbidItemIds } from './selectors'
import { getCurrentUserId } from '../session'

export const makeBid = (itemId: number, amount: number): ThunkAction =>
  async function(dispatch, getState) {
    const resource = `bids:${itemId}:create`
    const userId = getCurrentUserId(getState())
    if (!userId) return history.push('/login')
    if (isCommInProgress(resource, getState())) {
      return
    }
    dispatch(batch(CREATE_BID_REQUEST, startCommunication(resource)))
    try {
      const bid = await services[domains.BIDS].create({
        amount,
        userId,
        itemId
      })
      const { entities } = normalize(bid, schemas.bids)
      dispatch(
        batch(
          CREATE_BID_SUCCESS,
          mergeEntities(entities),
          endCommunication(resource, true)
        )
      )
      return true
    } catch (error) {
      // bid could fail if another user bids at the same time
      // because the server enforces every new bid must be greater
      // than the last/current one. For the moment stay on the
      // same page until we have UI to display a proper message
      console.warn('Create bid failed', error)
      dispatch(batch(CREATE_BID_FAILED, endCommunication(resource, false)))
      if (/token/.test(error.message)) {
        history.push('/myprofile/complete-profile')
      }
    }
  }

export const clearCurrentBids = () => {
  return batch(
    CLEAR_BIDS,
    sdel(BIDDING_SET.WON),
    sdel(BIDDING_SET.LOST),
    sdel(BIDDING_SET.WINNING),
    sdel(BIDDING_SET.OUTBID),
    removeAll(domains.BIDS)
  )
}

export const cleanCurrentBidsForAuction = (auctionId: number): ThunkAction => {
  return function(dispatch, getState) {
    // retrieve winning and outbid bids
    const winningItemIds = getWinningItemIds(getState())
    const outbidItemIds = getOutbidItemIds(getState())
    const allItems = getDomain(domains.ITEMS, getState())
    // filter those belonging to this auctionId out
    const remainingWinningIds = winningItemIds
      .map(id => allItems[id])
      .filter(item => item.auctionId !== auctionId)
      .map(item => item.id)
    const remainingOutbidIds = outbidItemIds
      .map(id => allItems[id])
      .filter(item => item.auctionId !== auctionId)
      .map(item => item.id)

    // store filtered results
    dispatch(
      batch(
        CLEAR_BIDS_FOR_AUCTION,
        sdel(BIDDING_SET.WINNING),
        sdel(BIDDING_SET.OUTBID),
        sadd(BIDDING_SET.WINNING, remainingWinningIds),
        sadd(BIDDING_SET.OUTBID, remainingOutbidIds)
      )
    )
  }
}

const getItemId = (bid: Bid) => bid.itemId

export const saveWinningBids = (bidsData: Array<Bid>) => {
  return addWinningItem(bidsData.map(getItemId))
}

export const saveOutbidBids = (bidsData: Array<Bid>) => {
  return addOutbidItem(bidsData.map(getItemId))
}

export const addWinningItem = (idOrArrayofIds: number | number[]) =>
  sadd(BIDDING_SET.WINNING, idOrArrayofIds)

export const removeWinningItem = (idOrArrayofIds: number | number[]) =>
  srem(BIDDING_SET.WINNING, idOrArrayofIds)
export const addOutbidItem = (idOrArrayofIds: number | number[]) =>
  sadd(BIDDING_SET.OUTBID, idOrArrayofIds)
export const removeOutbidItem = (id: number) => srem(BIDDING_SET.OUTBID, id)

export const addWonItem = (idOrArrayofIds: number | number[]) =>
  sadd(BIDDING_SET.WON, idOrArrayofIds)
export const addLostItem = (idOrArrayofIds: number | number[]) =>
  sadd(BIDDING_SET.LOST, idOrArrayofIds)

export const removeWonItems = (itemIds: number[]) =>
  srem(BIDDING_SET.WINNING, itemIds)
export const removeOutbidItems = (itemIds: number[]) =>
  srem(BIDDING_SET.OUTBID, itemIds)



// WEBPACK FOOTER //
// ./src/services/bid/actions.js